import { writable } from 'svelte/store';
import { browser } from '$app/environment';

import { ModalStyle } from '$shared/components';
import { modalService } from '$shared/lib/services/modalService';
import { modalStore } from '$shared/stores/modalStore';
import type { Modal } from '$shared/models/Modal';
import type { ModalEventFunction, ModalStyleOption } from '$shared/interfaces/IModal';

/**
 * Signal store incremented by module-level document event listeners.
 * Modal.svelte watches this via $effect and calls dispatchRefresh() whenever
 * it changes, mirroring the appLayoutStore pattern used for the splash screen.
 */
export const modalRefreshTick = writable(0);

// Register document event listeners at module-evaluation time so they are in
// place before any SvelteKit load() function runs. This eliminates the need
// for onMount in Modal.svelte and avoids lifecycle_outside_component errors.
if (browser) {
  document.addEventListener('app-modal:close', () => modalRefreshTick.update(n => n + 1));
  document.addEventListener('app-modal:refresh', () => modalRefreshTick.update(n => n + 1));
}

/**
 * Creates a modal instance without showing it. Replaces the setContext
 * 'createModalInstance' function previously exposed by Modal.svelte.
 *
 * Parameter order matches the original setContext API:
 *   (component, props, modalOptions, modalType, closeOnNavigate)
 */
export function createModalInstance(
  component: any,
  props: ObjectType<any>,
  modalOptions?: ModalStyleOption,
  modalType?: any,
  closeOnNavigate = true
): Modal {
  return modalService.add(modalType, {
    component,
    props: { ...props },
    options: modalOptions,
    show: false,
    closeOnNavigate
  });
}

/**
 * Updates props on all currently-visible modals and triggers a refresh.
 * Replaces the setContext 'updateModalProps' function.
 */
export function updateModalProps(props: ObjectType<any>): void {
  modalStore.list
    .filter((modal: Modal) => modal.show)
    .forEach((modal: Modal) => modal.updateProps(props));
  modalRefreshTick.update(n => n + 1);
}

/**
 * Registers an event handler on all currently-visible modals and triggers
 * a refresh. Replaces the setContext 'registerModalEvent' function.
 */
export function registerModalEvent(name: string, cb: ModalEventFunction): void {
  modalStore.list
    .filter((modal: Modal) => modal.show)
    .forEach((modal: Modal) => modal.registerModalEvent(name, cb));
  modalRefreshTick.update(n => n + 1);
}

/** Re-export ModalStyle as ModelStyles for backwards-compatibility with consumers. */
export { ModalStyle as ModelStyles };
